import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Award, 
  Users, 
  Globe, 
  CheckCircle,
  Star,
  MapPin,
  Phone
} from 'lucide-react';

const About: React.FC = () => {
  const achievements = [
    { number: "500+", label: "Successful Installations", icon: <CheckCircle className="h-8 w-8" /> },
    { number: "50+", label: "Expert Team Members", icon: <Users className="h-8 w-8" /> },
    { number: "10+", label: "Years of Experience", icon: <Award className="h-8 w-8" /> },
    { number: "35+", label: "Cities, Towns & Villages Served", icon: <Globe className="h-8 w-8" /> }
  ];

  const values = [
    {
      title: "Quality First",
      description: "We uphold the highest standards, using only premium components and materials to ensure long-lasting performance."
    },
    {
      title: "Customer Satisfaction",
      description: "Your satisfaction is our priority — we provide dedicated support, reliable service, and timely maintenance."
    },
    {
      title: "Innovation",
      description: "We continuously embrace the latest solar technologies and industry best practices to deliver smarter energy solutions."
    },
    {
      title: "Sustainability",
      description: "We are committed to building a cleaner, greener future for generations to come."
    }
  ];

  const certifications = [
    "ISO 9001:2015 Quality Management",
    "BIS Certified Solar Panels",
    "MNRE Approved Installer",
    "Electrical Safety Standards",
    "Environmental Management ISO 14001"
  ];

  return (
    <>
      <Helmet>
        <title>About AE Solar - Leading Solar Company in East Godavari, West Godavari & Visakhapatnam, Andhra Pradesh</title>
        <meta name="description" content="AE Solar is a leading solar panel installation company in East Godavari, West Godavari districts and Visakhapatnam (Vizag), Andhra Pradesh. 10+ years of experience, 500+ installations across Rajahmundry, Kakinada, Amalapuram, Eluru, Bhimavaram, Visakhapatnam and all towns in these districts." />
        <meta name="keywords" content="about AE Solar, solar company East Godavari, solar company West Godavari, solar installation Visakhapatnam, solar installation Vizag, solar installation company Kakinada, solar experts Rajahmundry, solar panel installers Andhra Pradesh" />
        <link rel="canonical" href="https://aesolarsystems.in/about" />
        <meta property="og:url" content="https://aesolarsystems.in/about" />
        <meta property="og:title" content="About AE Solar - Leading Solar Company in East Godavari, West Godavari & Visakhapatnam" />
        <meta property="og:description" content="AE Solar: 10+ years of experience, 500+ installations across East Godavari, West Godavari districts and Visakhapatnam (Vizag). Professional solar solutions with PM Solar Scheme subsidies." />
        <meta property="og:image" content="https://aesolarsystems.in/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About AE Solar" />
        <meta name="twitter:description" content="Leading solar company in East Godavari, West Godavari districts and Visakhapatnam (Vizag) with 10+ years of experience and 500+ successful installations." />
      </Helmet>
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url('https://img.freepik.com/premium-photo/blue-solar-photo-voltaic-panels-system-producing-renewable-clean-energy-rural-landscape-setting-sun_127089-3464.jpg?semt=ais_hybrid&w=740&q=80')`
        }}></div>
        
        {/* Dark Overlay for Better Text Visibility */}
        <div className="absolute inset-0 bg-blue-900/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl lg:text-5xl font-bold mb-6"
          >
            About AE Solar
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white max-w-3xl mx-auto font-medium"
          >
            Leading solar energy company in East Godavari, West Godavari districts and Visakhapatnam (Vizag), Andhra Pradesh, serving Rajahmundry, Kakinada, Amalapuram, Visakhapatnam, Eluru, Bhimavaram, Tanuku, Palakollu, Tuni, Peddapuram, Pithapuram, and all towns and villages across these districts. Committed to providing sustainable 
            energy solutions, solar panel installation, and exceptional customer service across the region.
          </motion.p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Founded in 2011, AE Solar began with a clear mission: to make solar energy 
                accessible and affordable for households and businesses across India.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                What began as a small team of passionate engineers has grown into one of the nation's 
                most trusted solar installation companies, proudly serving over 1,000 satisfied customers.
              </p>
              <p className="text-lg text-gray-600">
                Today, we continue to innovate and expand our services while upholding our core 
                commitment to quality, customer satisfaction, and environmental sustainability.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-gradient-to-br from-yellow-400 to-orange-500 p-8 rounded-2xl text-white"
            >
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-lg mb-4">
                To accelerate the adoption of solar energy across India by delivering affordable, reliable, and sustainable solar solutions.
              </p>
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-lg">
                To become a leading solar energy company in India, driving the nation's transition to clean and renewable energy.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Achievements
            </h2>
            <p className="text-xl text-gray-600">
              Numbers that speak for our success and growth
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="text-yellow-500 mb-4 flex justify-center">
                    {achievement.icon}
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {achievement.number}
                  </div>
                  <div className="text-gray-600">
                    {achievement.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="bg-gray-50 p-6 rounded-lg h-full">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-12 sm:py-16 lg:py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Certifications & Standards
            </h2>
            <p className="text-lg sm:text-xl text-gray-200 px-4">
              We maintain the highest industry standards and certifications
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-blue-800 p-4 sm:p-6 rounded-lg text-center"
              >
                <Star className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400 mx-auto mb-3" />
                <p className="text-sm sm:text-lg">{cert}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Service Areas
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 px-4">
              We proudly serve these cities, towns, and villages in East Godavari, West Godavari districts and Visakhapatnam, Andhra Pradesh
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
            {['Rajahmundry', 'Kakinada', 'Amalapuram', 'Visakhapatnam', 'Vizag', 'Eluru', 'Bhimavaram', 'Tanuku', 'Palakollu', 'Tadepalligudem', 'Nidadavole', 'Tuni', 'Peddapuram', 'Pithapuram', 'Mandapeta', 'Razole', 'Ramachandrapuram', 'Mummidivaram', 'Kothapeta', 'Samalkot', 'Yeleswaram', 'Gollaprolu', 'Tallarevu', 'Uppalaguptam', 'Malikipuram', 'Allavaram', 'Katrenikona', 'I. Polavaram', 'Devipatnam', 'Maredumilli', 'Y. Ramavaram', 'Addateegala', 'Rajanagaram', 'Anaparthy', 'Kadiam', 'Sankhavaram', 'Kotananduru', 'Alamuru', 'Atreyapuram', 'Ravulapalem', 'Kapileswarapuram', 'Karapa', 'Thondangi', 'Sakhinetipalli', 'Ainavilli', 'Ambajipeta'].map((city, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 p-3 sm:p-4 rounded-lg text-center hover:bg-yellow-50 transition-colors cursor-pointer"
              >
                <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-500 mx-auto mb-2" />
                <p className="font-medium text-gray-900 text-sm sm:text-base">{city}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 px-4">
            Ready to Work with Us?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Join our growing family of satisfied customers and experience the AE Solar difference
          </p>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
